import { Link } from "react-router-dom";
import { PRODUCTS } from "@/lib/data";
import { Zap, Battery, Sun, Cpu, Fuel, Snowflake, ArrowUpRight, ArrowRight } from "lucide-react";

const ICONS = {
  "P-01": Zap,
  "P-02": Battery,
  "P-03": Sun,
  "P-04": Cpu,
  "P-05": Fuel,
  "P-06": Snowflake,
};

export default function ProductsPage() {
  return (
    <>
      {/* Page hero */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 border-b border-damita-border relative overflow-hidden">
        <div className="absolute inset-0 -z-10 blueprint-bg opacity-50" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-damita-blue mb-6 flex items-center gap-3">
            <span className="w-8 tick-line" /> Product Portfolio · 2025
          </div>
          <div className="grid grid-cols-12 gap-6 lg:gap-12 items-end">
            <h1 className="col-span-12 lg:col-span-8 font-display font-black text-4xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter">
              Our <span className="text-damita-blue">products.</span>
            </h1>
            <p className="col-span-12 lg:col-span-4 text-base md:text-lg leading-relaxed text-damita-ink2">
              Premium, highly reliable equipment designed to safeguard your infrastructure
              and guarantee uninterrupted operations.
            </p>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section data-testid="products-section" className="border-b border-damita-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <div className="grid grid-cols-12 gap-px bg-damita-border border border-damita-border">
            {PRODUCTS.map((p) => {
              const Icon = ICONS[p.code] || Zap;
              return (
                <div
                  key={p.code}
                  data-testid={`product-${p.code}`}
                  className="col-span-12 md:col-span-6 lg:col-span-4 bg-white p-6 md:p-8 group hover:bg-damita-blue hover:text-white transition-colors"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-12 h-12 grid place-items-center bg-damita-surface group-hover:bg-white/10 transition-colors">
                      <Icon size={22} strokeWidth={2} />
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-70">{p.code}</div>
                      <ArrowUpRight size={20} className="ml-auto mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-3">{p.title}</h3>
                  <p className="text-sm md:text-base leading-relaxed opacity-80 mb-5">{p.blurb}</p>
                  <ul className="space-y-2.5 border-t border-damita-border group-hover:border-white/20 pt-5">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-sm">
                        <span className="mt-1.5 w-3 h-px bg-damita-ink2 group-hover:bg-damita-yellow shrink-0" />
                        <span className="opacity-90">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-damita-blue text-white border-b border-damita-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-14 md:py-20 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-8">
            <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
              Need a configuration<br />
              <span className="text-damita-yellow">for your specific site?</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 flex">
            <Link
              data-testid="products-cta-quote"
              to="/contact"
              className="inline-flex items-center justify-between gap-3 bg-damita-yellow text-white px-6 py-4 font-semibold hover:bg-[#D96B12] transition-all btn-press w-full"
            >
              Request a Quote
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
